import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * A dashboard section.
 *
 * `collapsible` puts the section behind a disclosure that still shows its count, so a
 * closed section says how much it holds. Every dashboard section uses it; the archive
 * starts closed.
 *
 * `weight` gives the board a centre: the section people come for is `primary` (orange top
 * line, slightly larger title), the archive is `quiet` (just a line until opened).
 *
 * An empty section is one line: its title and a 0, nothing to open.
 */
export function Section({
  title,
  hint,
  count,
  children,
  collapsible = false,
  open = true,
  onToggle,
  weight = 'default',
}: {
  title: string
  hint?: string
  count: number
  children: ReactNode
  collapsible?: boolean
  open?: boolean
  onToggle?: () => void
  weight?: 'primary' | 'default' | 'quiet'
}) {
  const empty = count === 0
  const quiet = weight === 'quiet' || empty
  collapsible = collapsible && !empty
  const header = (
    <div className="flex w-full items-center gap-2.5">
      {collapsible && (
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        />
      )}
      <h2
        className={
          weight === 'primary'
            ? 'text-base font-semibold text-slate-900 dark:text-slate-100'
            : quiet
              ? 'text-sm font-medium text-slate-600 dark:text-slate-400'
              : 'text-[15px] font-semibold text-slate-900 dark:text-slate-100'
        }
      >
        {title}
      </h2>
      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 tabular-nums dark:bg-slate-800 dark:text-slate-300">
        {count}
      </span>
      {hint && (
        <span className="ml-auto hidden truncate text-xs text-slate-600 lg:inline dark:text-slate-400">
          {hint}
        </span>
      )}
    </div>
  )

  return (
    <section
      className={
        quiet
          ? open
            ? 'overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
            : ''
          : `overflow-hidden rounded-xl border bg-white dark:bg-slate-900 ${
              weight === 'primary'
                ? 'border-slate-300 border-t-[3px] border-t-indigo-500 dark:border-slate-700 dark:border-t-indigo-500'
                : 'border-slate-200 dark:border-slate-800'
            }`
      }
    >
      {collapsible ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className={`flex w-full items-center text-left transition-colors ${
            quiet && !open
              ? 'rounded-lg px-4 py-2 hover:bg-slate-200/60 dark:hover:bg-slate-800/60'
              : 'px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40'
          }`}
        >
          {header}
        </button>
      ) : empty ? (
        <div className="px-4 py-2">{header}</div>
      ) : (
        <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">{header}</div>
      )}
      {!empty && (!collapsible || open) && (
        <div
          className={collapsible ? 'border-t border-slate-200 dark:border-slate-800' : undefined}
        >
          {children}
        </div>
      )}
    </section>
  )
}

/** A day heading inside a section ("Heute", "Morgen", ...), with how many it holds. */
export function GroupHeading({
  title,
  count,
  tone = 'default',
}: {
  title: string
  count: number
  tone?: 'default' | 'warning'
}) {
  return (
    <div
      className={`flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-1.5 text-xs font-semibold dark:border-slate-800 dark:bg-slate-800/40 ${
        tone === 'warning'
          ? 'text-rose-700 dark:text-rose-400'
          : 'text-slate-600 dark:text-slate-300'
      }`}
    >
      {title}
      <span className="font-normal text-slate-500 tabular-nums dark:text-slate-400">{count}</span>
    </div>
  )
}
